'use client';

import { QueryClient, QueryClientProvider, type DefaultOptions } from '@tanstack/react-query'
import { PropsWithChildren, useState } from 'react'
import { useQueryClientInstance } from '@/hooks/useQueryClientInstance'

// Configure default options for React Query with extremely conservative settings
// This will prevent unnecessary refetches when visiting the same pages
const defaultOptions: DefaultOptions = {
  queries: {
    // Completely disable automatic refetching
    refetchOnWindowFocus: false, // Never refetch when window regains focus
    refetchOnMount: false, // Never refetch when component mounts
    refetchOnReconnect: false, // Never refetch when network reconnects
    retry: false, // Don't retry failed queries - manual retry only

    // Use very long cache times to ensure data persists between visits
    staleTime: 1000 * 60 * 60 * 24 * 30, // 30 days before data is considered stale
    gcTime: 1000 * 60 * 60 * 24 * 60, // 60 days before unused data is garbage collected
  },
  mutations: {
    retry: false, // Don't retry failed mutations
  },
}

/**
 * React Query provider component with aggressive caching to prevent unnecessary fetches
 *
 * This configuration ensures:
 * 1. Data stays fresh for a very long time (30 days)
 * 2. No automatic refetching occurs on mount, focus, or reconnect
 * 3. Each user gets their own isolated cache instance
 */
export function QueryProvider({ children }: PropsWithChildren) {
  // Create a client for each session to avoid shared state across users
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: defaultOptions,
      })
  )

  return (
    <QueryClientProvider client={queryClient}>
      <QueryClientInitializer />
      {children}
      {/* TanStack Query devtools disabled for this branch */}
    </QueryClientProvider>
  )
}

/**
 * Component that initializes the query client instance
 * This makes the query client available globally for non-component contexts
 */
function QueryClientInitializer() {
  useQueryClientInstance();
  return null;
}